import { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import Section from "../../common/section";
import {
  learningObjectCountSummaryRequest,
  learningObjectItemSummaryRequest,
  learningObjectUsersRequest,
  setLearningObjectQuery,
  learningObjectUserExcelRequest,
} from "../../../store/slices/reportSlice";
import LearningStatusSummary from "./widgets/LearningStatusSummary";
import LearningItemSummary from "./widgets/LearningItemSummary";
import LearningUsersSummary from "./widgets/LearningUsersSummary";
import DropdownButton from "../../common/dropDownButton";

const LearningObjectReport = ({
  additionalQuery,
  filters = [],
  learningObjects,
  loadLearningObjectCountSummary,
  loadLearningObjectItemSummary,
  loadLearningObjectUsers,
  setLearningObjectQuery,
  loadLearningObjectUserExcel,
}) => {
  const intl = useIntl();

  const fetchReports = useCallback(() => {
    const payloadFilters = filters
      .filter((item) => item.values.length > 0)
      .map((item) => ({
        id: item.id,
        values: item.values,
        isDynamicField:
          item.id !== "Designation" &&
          item.id !== "Department" &&
          item.id !== "ManagerId",
      }));

    loadLearningObjectCountSummary({
      query: additionalQuery,
      filters: payloadFilters,
    });
    loadLearningObjectItemSummary({
      query: additionalQuery,
      filters: payloadFilters,
    });
  }, [
    filters,
    loadLearningObjectCountSummary,
    loadLearningObjectItemSummary,
    setLearningObjectQuery,
    additionalQuery,
  ]);

  useEffect(() => {
    if (learningObjects.query.pageNo !== 1) {
      setLearningObjectQuery({
        ...learningObjects.query,
        pageNo: 1,
      });
    }
  }, [filters]);

  const fetchUserList = useCallback(() => {
    const payloadFilters = filters
      .filter((item) => item.values.length > 0)
      .map((item) => ({
        id: item.id,
        values: item.values,
        isDynamicField:
          item.id !== "Designation" &&
          item.id !== "Department" &&
          item.id !== "ManagerId",
      }));
    loadLearningObjectUsers({
      query: {
        ...learningObjects.query,
        ...additionalQuery,
      },
      filters: payloadFilters,
    });
  }, [learningObjects.query, filters, additionalQuery]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  const exportReport = (fileType = "excel") => {
    loadLearningObjectUserExcel({
      fileType,
      query: {
        ...learningObjects.query,
        ...additionalQuery,
      },
      filters: filters
        .filter((item) => item.values.length > 0)
        .map((item) => ({
          id: item.id,
          values: item.values,
          isDynamicField:
            item.id !== "Designation" &&
            item.id !== "Department" &&
            item.id !== "ManagerId",
        })),
    });
  };

  return (
    <div className="grid">
      <div className="col-12 md:col-4">
        <Section title="summary">
          <LearningStatusSummary
            loading={learningObjects?.countSummary === null}
            data={learningObjects?.countSummary || []}
          />
        </Section>
      </div>
      <div className="col-12 md:col-8">
        <Section title="by_learning_objects">
          <LearningItemSummary
            loading={learningObjects?.itemSummary === null}
            data={learningObjects?.itemSummary || []}
          />
        </Section>
      </div>
      <div className="col-12">
        <Section title="details">
          <div
            className="flex justify-content-end"
            style={{ marginBottom: 10 }}
          >
            <DropdownButton
              icon="pi pi-download"
              label={intl.formatMessage({ id: "export" })}
              items={[
                {
                  label: intl.formatMessage({ id: "excel" }),
                  command: () => exportReport("excel"),
                },
                {
                  label: intl.formatMessage({ id: "csv" }),
                  command: () => exportReport("csv"),
                },
                {
                  label: intl.formatMessage({ id: "pdf" }),
                  command: () => exportReport("pdf"),
                },
              ]}
            />
          </div>
          <LearningUsersSummary
            statusLabel="learning_object_status"
            showLearningProgramDetails={true}
            loading={learningObjects?.list?.list === null}
            data={learningObjects?.list?.list || []}
            totalRecords={learningObjects?.list?.totalCount || 0}
            query={learningObjects.query}
            setQuery={setLearningObjectQuery}
          />
        </Section>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  learningObjects: state.reports.learningObjects,
});

const mapDispatchToProps = (dispatch) => ({
  loadLearningObjectCountSummary: (data) =>
    dispatch(learningObjectCountSummaryRequest(data)),
  loadLearningObjectItemSummary: (data) =>
    dispatch(learningObjectItemSummaryRequest(data)),
  loadLearningObjectUsers: (data) => dispatch(learningObjectUsersRequest(data)),
  setLearningObjectQuery: (data) => dispatch(setLearningObjectQuery(data)),
  loadLearningObjectUserExcel: (data) =>
    dispatch(learningObjectUserExcelRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningObjectReport);
